import React, { useState, useEffect } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
	ScatterChart,
	Scatter,
	ZAxis,
	AreaChart,
	Area,
} from 'recharts';
import TableLoading from '../../components/TableLoading';
import { formatDuration, mapTimeRangeToApiFilterAlt3, request } from '../../services/utilities';
import { analyticsAPI } from '../../services/constants';
import { TimeRangeDropdownAlt3 } from '../../components/TimeRangeDropdownAlt3';
import SlidingPane from '../../components/SlidingPane';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff6e54', '#ffc0cb', '#faad14'];

const ImmunizationOverview = () => {
	const [timeRanges, setTimeRanges] = useState({
		immunizationCount: 'past30Days',
		immunizationByDoctor: 'past30Days',
		immunizationByNurse: 'past30Days',
		immunizationTopBottom: 'past30Days',
		immunizationTrends: 'past30Days',
		staffAverageTime: 'past30Days',
	});

	const [immunizationCount, setImmunizationCount] = useState(null);
	const [immunizationByDoctor, setImmunizationByDoctor] = useState([]);
	const [immunizationByNurse, setImmunizationByNurse] = useState([]);
	const [topImmunization, setTopImmunization] = useState([]);
	const [bottomImmunization, setBottomImmunization] = useState([]);
	const [immunizationTrends, setImmunizationTrends] = useState([]);
	const [staffAverageTime, setStaffAverageTime] = useState(null);
	const [modalAlt, setModalAlt] = useState(false);

	const [loadingStates, setLoadingStates] = useState({
		immunizationCount: false,
		immunizationByDoctor: false,
		immunizationByNurse: false,
		immunizationTopBottom: false,
		immunizationTrends: false,
		staffAverageTime: false,
	});

	// Immunization Count
	const fetchImmunizationCount = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationCount: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-count?dateFilter=${dateFilter}`, 'GET', true);
			// const apiResponse = parseInt(res?.result);
			console.log(res);
			setImmunizationCount(res?.result);
		} catch (error) {
			console.error('Failed to fetch immunization count:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationCount: false }));
		}
	};

	// Immunization By Doctor
	const fetchImmunizationByDoctor = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationByDoctor: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-by-staff?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.doctor || 'Unknown',
					count: parseInt(item?.count),
				}));
			setImmunizationByDoctor(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization by doctor:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationByDoctor: false }));
		}
	};

	// Immunization By Nurse
	const fetchImmunizationByNurse = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationByNurse: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-by-nurse?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.doctor || 'Unknown',
					count: parseInt(item?.count),
				}))
				.slice(0, 30);
			setImmunizationByNurse(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization by doctor:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationByNurse: false }));
		}
	};

	// Immunization Top & Bottom
	const fetchImmunizationTopBottom = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationTopBottom: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-top-bottom?dateFilter=${dateFilter}`, 'GET', true);
			console.log(res.result);
			const apiResponseTop = res?.result?.map(x => ({
				name: x?.vaccine,
				count: parseInt(x?.count),
			}));

			const apiResponseBottom = res?.result?.map(x => ({
				name: x?.vaccine,
				count: parseInt(x?.count),
			}));
			setTopImmunization(apiResponseTop);
			setBottomImmunization(apiResponseBottom);
		} catch (error) {
			console.error('Failed to fetch immunization top-bottom:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationTopBottom: false }));
		}
	};

	// Immunization Trends
	const fetchImmunizationTrends = async range => {
		setLoadingStates(prev => ({ ...prev, immunizationTrends: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/immunization-trends?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.date || item?.year || item?.month || 'Unknown',
					count: parseInt(item?.count),
				}));
			setImmunizationTrends(apiResponse ?? []);
		} catch (error) {
			console.error('Failed to fetch immunization trends:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, immunizationTrends: false }));
		}
	};

	// Staff Average Time
	const fetchStaffAverageTime = async range => {
		setLoadingStates(prev => ({ ...prev, staffAverageTime: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`${analyticsAPI}/staff-activity?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponse = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(item => ({
					name: item?.username || 'Unknown',
					count: parseFloat(item?.avgTimeSpent),
				}));
			setStaffAverageTime(apiResponse);
		} catch (error) {
			console.error('Failed to fetch staff average time:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, staffAverageTime: false }));
		}
	};

	// Initial data load
	useEffect(() => {
		fetchImmunizationCount(timeRanges.immunizationCount);
		fetchImmunizationByDoctor(timeRanges.immunizationByDoctor);
		fetchImmunizationByNurse(timeRanges.immunizationByNurse);
		fetchImmunizationTopBottom(timeRanges.immunizationTopBottom);
		fetchImmunizationTrends(timeRanges.immunizationTrends);
		fetchStaffAverageTime(timeRanges.staffAverageTime);
	}, []);

	// Handle time range changes
	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'immunizationCount':
				fetchImmunizationCount(range);
				break;
			case 'immunizationByDoctor':
				fetchImmunizationByDoctor(range);
				break;
			case 'immunizationByNurse':
				fetchImmunizationByNurse(range);
				break;
			case 'immunizationTopBottom':
				fetchImmunizationTopBottom(range);
				break;
			case 'immunizationTrends':
				fetchImmunizationTrends(range);
				break;
			case 'staffAverageTime':
				fetchStaffAverageTime(range);
				break;
			default:
				break;
		}
	};

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{formatDuration(value)}
			</text>
		);
	};

	const renderCustomLabel = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value}
			</text>
		);
	};

	const renderCustomLabelAlt2 = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString() + 'hr'}
			</text>
		);
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	const CustomToolTipAlt = ({ active, payload, label }) => {
		if (!active || !payload?.length) return null;

		const data = payload[0]?.payload;

		return (
			<div className="custom-tooltip" style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: 10 }}>
				<p>
					<strong>{String(label).charAt(0).toUpperCase() + String(label).slice(1)}</strong>
				</p>
				<p>Average Turn Around Time: {Number(data?.avgTATMinutes).toLocaleString() + 'mins'}</p>
			</div>
		);
	};

	return (
		<div className="content-">
			<div className="content-bo">
				<div className="row">
					{/* Immunization */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header"> Immunization Overview </h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges?.immunizationCount} onChange={handleTimeRangeChange} chartId="immunizationCount" />
							</div>
							<p className="chart-description card p-2">Summary metrics showing overall Immunization activity.</p>

							<div className="element-box">
								{loadingStates.immunizationCount ? (
									<TableLoading />
								) : (
									<div className="row">
										<div className="col-md-2 col-sm-2 col-lg-2">
											<div className="value text-primary" style={{ fontSize: '25px' }}>
												{immunizationCount?.total?.toLocaleString() || 0}
											</div>
											<div className="label">Total</div>
										</div>
										<div className="col-md-2 col-sm-2 col-lg-2">
											<div className="value text-warn" style={{ fontSize: '25px' }}>
												{immunizationCount?.pending?.toLocaleString() || 0}
											</div>
											<div className="label">Pending</div>
										</div>
										<div className="col-md-2 col-sm-2 col-lg-2">
											<div className="value text-success" style={{ fontSize: '25px' }}>
												{immunizationCount?.administered?.toLocaleString() || 0}
											</div>
											<div className="label">Administered</div>
										</div>
										<div className="col-md-2 col-sm-2 col-lg-2">
											<div className="value text-danger" style={{ fontSize: '25px' }}>
												{immunizationCount?.overdue?.toLocaleString() || 0}
											</div>
											<div className="label">Overdue</div>
										</div>
										<div className="col-md-2 col-sm-2 col-lg-2">
											<div className="value text-info" style={{ fontSize: '25px' }}>
												{immunizationCount?.scheduled?.toLocaleString() || 0}
											</div>
											<div className="label">Scheduled</div>
										</div>
									</div>
								)}
								{/* <div className="row">
									<div className="col-md-2 col-sm-2 col-lg-2">
										<div className="value text-primary">{immunizationCount?.toLocaleString() || 0}</div>
										<div className="label">Total Immunization Count.</div>
									</div>
								</div> */}
							</div>
						</div>
					</div>

					{/* Immunization Trends */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Immunization Trends</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3
									value={timeRanges.immunizationTrends}
									onChange={handleTimeRangeChange}
									chartId="immunizationTrends"
								/>
							</div>
							<p className="chart-description mb-3 p-2 card">Visual trend of immunization activity over time.</p>

							<div className="element-box">
								{loadingStates.immunizationTrends ? (
									<TableLoading />
								) : immunizationTrends?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={immunizationTrends} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.immunizationTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#FFBB28"
													fill="#FFBB28"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabel}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Immunization By Doctor */}
					{/* <div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Immunization By Doctor</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3
									value={timeRanges.immunizationByDoctor}
									onChange={handleTimeRangeChange}
									chartId="immunizationByDoctor"
								/>
							</div>
							<p className="chart-description mb-3 p-2 card">Breakdown of immunizations administered by each Doctor.</p>

							<div className="element-box">
								{loadingStates?.immunizationByDoctor ? (
									<TableLoading />
								) : immunizationByDoctor?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={immunizationByDoctor} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#8884d8"
													fill="#8884d8"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabel}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div> */}

					{/* Immunization By Nurse */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Immunization By Nurse</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3
									value={timeRanges.immunizationByNurse}
									onChange={handleTimeRangeChange}
									chartId="immunizationByNurse"
								/>
							</div>
							<p className="chart-description mb-3 p-2 card">Breakdown of immunizations administered by each Nurse.</p>

							<div className="element-box">
								{loadingStates?.immunizationByNurse ? (
									<TableLoading />
								) : immunizationByNurse?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={immunizationByNurse} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#8884d8"
													fill="#8884d8"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabel}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* Top Immunization */}
					<div className="col-sm-6 col-lg-">
						<div className="element-wrapper">
							<h6 className="element-header">Top 10 Immunization</h6>
							<div className="d-flex justify-content-end w-100">
								{/* <div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											// onClick={() => setModalAlt(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div> */}
								{/* <TimeRangeDropdownAlt3
									value={timeRanges.immunizationTopBottom}
									onChange={handleTimeRangeChange}
									chartId="immunizationTopBottom"
								/> */}
							</div>
							<p style={{ marginTop: '50px' }} className="chart-description mb-3 p-2 card">
								Chart showing the top performing immunization by count.
							</p>

							<div className="element-box">
								{loadingStates?.immunizationTopBottom ? (
									<TableLoading />
								) : topImmunization?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={topImmunization?.slice(0, 10)} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#0088FE"
													fill="#0088FE"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabel}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>
					<div className="col-sm-6 col-lg-">
						<div className="element-wrapper">
							<h6 className="element-header">Bottom 10 Immunization</h6>
							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								<TimeRangeDropdownAlt3
									value={timeRanges.immunizationTopBottom}
									onChange={handleTimeRangeChange}
									chartId="immunizationTopBottom"
								/>
							</div>

							<p className="chart-description mb-3 p-2 card">Chart showing the bottom performing immunization by count.</p>

							<div className="element-box">
								{loadingStates?.immunizationTopBottom ? (
									<TableLoading />
								) : topImmunization?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={topImmunization?.slice(-10)} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#0088FE"
													fill="#0088FE"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabel}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
						<SlidingPane isOpen={modalAlt}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModalAlt(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdownAlt3
										value={timeRanges.immunizationTopBottom}
										onChange={handleTimeRangeChange}
										chartId="immunizationTopBottom"
									/>
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Immunization count </h6>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">Chart showing the immunization count .</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loadingStates?.immunizationTopBottom ? (
															<TableLoading />
														) : topImmunization?.length > 0 ? (
															<div style={{ height: '400px' }}>
																<ResponsiveContainer>
																	<AreaChart data={topImmunization} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
																		<CartesianGrid strokeDasharray="3 3" />
																		<XAxis
																			dataKey={'name'}
																			angle={-45}
																			textAnchor="end"
																			fontSize={12}
																			interval={0}
																			tickFormatter={value => {
																				const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																				const capitalized = truncated
																					.toLowerCase()
																					.split(' ')
																					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																					.join(' ');
																				return capitalized;
																			}}
																		/>
																		<YAxis domain={[0, 'auto']} />
																		<Tooltip
																			formatter={value => Number(value).toLocaleString()}
																			labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
																		/>
																		<Legend wrapperStyle={{ marginBottom: '-50px' }} />
																		<Area
																			type="monotone"
																			dataKey="count"
																			stroke="#0088FE"
																			fill="#0088FE"
																			strokeWidth={2}
																			dot={{ r: 4 }}
																			activeDot={{ r: 6 }}
																		>
																			<LabelList
																				dataKey="count"
																				content={renderCustomLabel}
																				position="top"
																				style={{ fontSize: '12px', fill: '#333' }}
																			/>
																		</Area>
																	</AreaChart>
																</ResponsiveContainer>
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/* Bottom Immunization */}
					{/* <div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Bottom Immunization</h6>
							<div className="d-flex justify-content-end w-100">
								<div>
										<div
											style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
											// onClick={() => setModalAlt(true)}
											className="form-control form-control-sm text-white bg-primary rounded"
										>
											View All
										</div>
									</div>
								<TimeRangeDropdownAlt3
									value={timeRanges.immunizationTopBottom}
									onChange={handleTimeRangeChange}
									chartId="immunizationTopBottom"
								/>
							</div>
							<p className="chart-description mb-3 p-2 card">Chart showing the bottom performing doctors based on immunization count.</p>

							<div className="element-box">
								{loadingStates?.immunizationTopBottom ? (
									<TableLoading />
								) : bottomImmunization?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={bottomImmunization?.slice(-10)} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-50px' }} />
												<Area
													type="monotone"
													dataKey="count"
													stroke="#0088FE"
													fill="#0088FE"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabel}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div> */}

					{/* Staff average active time*/}
					{/* <div className="col-sm-12 ">
						<div className="element-wrapper">
							<h6 className="element-header">Staff Average Active Time</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdownAlt3 value={timeRanges.staffAverageTime} onChange={handleTimeRangeChange} chartId="staffAverageTime" />
							</div>
							<p className="chart-description mb-3 p-2 card">Chart showing the bottom performing doctors based on immunization count.</p>

							<div className="element-box">
								{loadingStates?.staffAverageTime ? (
									<TableLoading />
								) : staffAverageTime?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<LineChart data={staffAverageTime.slice(0, 20)} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => formatDuration(value)}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Line
													type="monotone"
													dataKey="count"
													name="Average Time Spent"
													stroke="#22BB33"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '10px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>  */}
				</div>
			</div>
		</div>
	);
};

export default ImmunizationOverview;
